/**
 * Modal Manager Hook and Provider
 *
 * Centralized modal coordination system using a stack-based (LIFO) approach.
 * Prevents multiple popups from being open simultaneously and provides
 * navigation queuing and loading gate synchronization.
 *
 * Features:
 * - Stack management (LIFO) for nested modals
 * - Auto z-index assignment (base 1000 + position * 10)
 * - Navigation queue (FIFO) - defers navigation until modals close
 * - Loading gates - replaces arbitrary timeouts with event-driven waits
 *
 * Usage:
 * ```tsx
 * // In App.tsx
 * <ModalProvider>
 *   <YourApp />
 * </ModalProvider>
 *
 * // In components
 * const { openModal, closeModal, isModalOpen } = useModalManager();
 * ```
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  ModalId,
  ModalManagerContextValue,
  ModalPriority,
  ModalStackItem,
  NavigationAction,
} from '../types/modal-manager.types';
import { loadingGateService } from '../services/LoadingGateService';
import { navigationQueueService } from '../services/NavigationQueueService';
import { logger } from '@/utils/logger';

// Z-index constants
const BASE_Z_INDEX = 1000;
const Z_INDEX_INCREMENT = 10;

// Create context
const ModalManagerContext = createContext<ModalManagerContextValue | undefined>(
  undefined
);

/**
 * Modal Manager Provider Component
 */
export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [stack, setStack] = useState<ModalStackItem[]>([]);

  /**
   * Open a modal with optional priority
   */
  const openModal = useCallback(
    (id: ModalId, priority: ModalPriority = ModalPriority.NORMAL) => {
      setStack(prevStack => {
        // Check if modal is already open
        if (prevStack.some(item => item.id === id)) {
          logger.ui.warn(`[ModalManager] Modal "${id}" is already open`);
          return prevStack;
        }

        // Calculate z-index based on stack position
        const zIndex = BASE_Z_INDEX + prevStack.length * Z_INDEX_INCREMENT;

        const newItem: ModalStackItem = {
          id,
          priority,
          openedAt: Date.now(),
          zIndex,
        };

        return [...prevStack, newItem];
      });
    },
    []
  );

  /**
   * Close a specific modal
   */
  const closeModal = useCallback((id: ModalId) => {
    setStack(prevStack => {
      const filtered = prevStack.filter(item => item.id !== id);

      // If we removed the last modal and there are queued navigation actions, flush them
      if (filtered.length === 0 && navigationQueueService.hasPending()) {
        // Defer execution to next tick to allow React to complete modal cleanup
        setTimeout(() => {
          const actions = navigationQueueService.flush();
          logger.ui.debug(
            `[ModalManager] Flushing ${actions.length} queued navigation action(s)`,
            { actions }
          );
          // Note: Actions are flushed but execution is handled by DeepLinkProvider/Dashboard
          // The consuming component should check navigationQueueService.hasPending()
        }, 0);
      }

      return filtered;
    });
  }, []);

  /**
   * Close all modals
   */
  const closeAll = useCallback(() => {
    setStack([]);

    // Flush navigation queue when all modals close
    if (navigationQueueService.hasPending()) {
      setTimeout(() => {
        const actions = navigationQueueService.flush();
        logger.ui.debug(
          `[ModalManager] Flushing ${actions.length} queued navigation action(s) after closing all modals`,
          { actions }
        );
      }, 0);
    }
  }, []);

  /**
   * Check if a specific modal is open
   */
  const isModalOpen = useCallback(
    (id: ModalId): boolean => {
      return stack.some(item => item.id === id);
    },
    [stack]
  );

  /**
   * Get the z-index for a modal
   */
  const getZIndex = useCallback(
    (id: ModalId): number => {
      const item = stack.find(item => item.id === id);
      return item?.zIndex ?? BASE_Z_INDEX;
    },
    [stack]
  );

  /**
   * Get the current modal stack
   */
  const getStack = useCallback((): ModalStackItem[] => {
    return [...stack];
  }, [stack]);

  /**
   * Queue a navigation action to be executed after modals close
   */
  const queueNavigation = useCallback((action: NavigationAction) => {
    navigationQueueService.enqueue(action);
    logger.ui.debug(`[ModalManager] Navigation queued`, { action });
  }, []);

  /**
   * Register a loading gate
   */
  const registerLoadingGate = useCallback(
    (gateId: string, isLoading: boolean) => {
      loadingGateService.register(gateId, isLoading);
    },
    []
  );

  /**
   * Wait for a loading gate to open
   */
  const waitForGate = useCallback(
    (gateId: string, timeoutMs?: number): Promise<void> => {
      return loadingGateService.wait(gateId, timeoutMs);
    },
    []
  );

  /**
   * Handle Escape key to close top modal
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && stack.length > 0) {
        // Close the top modal (last in stack)
        const topModal = stack[stack.length - 1];
        closeModal(topModal.id);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [stack, closeModal]);

  /**
   * Dev mode: Log stack changes
   */
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (stack.length > 0) {
        logger.ui.debug(`[ModalManager] Stack updated`, {
          stack: stack.map(item => item.id),
        });
      }
    }
  }, [stack]);

  const value: ModalManagerContextValue = {
    openModal,
    closeModal,
    closeAll,
    isModalOpen,
    getZIndex,
    getStack,
    queueNavigation,
    registerLoadingGate,
    waitForGate,
  };

  return (
    <ModalManagerContext.Provider value={value}>
      {children}
    </ModalManagerContext.Provider>
  );
};

/**
 * Hook to access Modal Manager
 */
// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export const useModalManager = (): ModalManagerContextValue => {
  const context = useContext(ModalManagerContext);

  if (!context) {
    throw new Error('useModalManager must be used within a ModalProvider');
  }

  return context;
};

/**
 * Optional: Hook to auto-track modal open/close
 * Automatically calls openModal on mount and closeModal on unmount
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with provider
export const useModalTracking = (
  modalId: ModalId,
  priority?: ModalPriority
): void => {
  const { openModal, closeModal } = useModalManager();

  useEffect(() => {
    openModal(modalId, priority);
    return () => closeModal(modalId);
  }, [modalId, priority, openModal, closeModal]);
};
